<template>
  <div class="container-fluid">
    <div class="row page-titles">
      <div class="col-md-5 col-8 align-self-center">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/home">Home</router-link>
          </li>
          <li class="breadcrumb-item active">シフト表</li>
        </ol>
      </div>
    </div>
    <div class="row">
      <div class="col-md-3 pr-10">
        <div class="mt-10 card">
          <div class="card-body">
              <div>
                <p class="mb-3 text-left">事業所</p>
                <select-office v-model="inputs.selectedOfficeId" :disabled="isProcessing"
                  :allowClear="false"
                  @update:modelValue="onOfficeSelected"/>
              </div>
              <div class="pt-2" v-if="false">
                <p class="mb-0">利用者表示</p>
                <div class="switch pt-1">
                  <label>
                    全利用者
                    <input type="checkbox" v-model="inputs.userViewMode" @change.prevent="onChangeUserViewMode"><span
                      class="lever"></span>
                    担当利用者のみ
                  </label>
                </div>
                <div>
                  <p class="text-right">
                    <router-link :to="`/staff/${profile.id}`">担当利用者を設定する</router-link>
                  </p>
                </div>
              </div>
              <div class="pt-2">
                <p class="mb-0 text-left">スタッフ一覧</p>
                <staff-search @change="addSelectedStaff" :staffs="staffs" :clear-after-selected="true"
                  :disabled="isProcessing || !(inputs.selectedOfficeId)" />
                <staff-check-box-list v-model="inputs.selectedStaffs"
                  :disabled="isProcessing || !(inputs.selectedOfficeId)" />
              </div>
          </div>
        </div>
      </div>
      <div class="col-md-9 h-83vh">
        <div class="card h-100">
          <div class="card-body staff-shift" :class="{'staff-shift-week': isWeekMode, 'staff-shift-day': !isWeekMode}" @mousemove="mousemove($event)">
            <div class="tooltipevent" v-if="tip.active" :style="tipPlace">
              利用者：{{ tip.event.extendedProps.userDisplayName || tip.event.extendedProps.tag?.user?.display_name }}<br>
              時間：{{ tip.event.extendedProps.caretime }}<br>
              サービス：{{ tip.event.extendedProps.service }}<br>
            </div>
            <FullCalendar :options='calendarOptions' ref="calendarRef">
              <template v-slot:eventContent='arg'>
                <div class="fc-event-main-content">
                  <div class="fc-event-time-title">
                    {{ formatEventTime(arg.event.start) }} - {{ formatEventTime(arg.event.end) }}
                  </div>
                  <div class="fc-event-time-title fc-event-user-name">
                    {{ arg.event.title }}
                  </div>
                  <div v-if="arg.event.extendedProps && arg.event.extendedProps.service" class="event-type"
                    :style="{ display: arg.view.type === 'resourceTimeGridDay' ? 'block' : 'none' }">
                    <small>{{ arg.event.extendedProps.service }}</small>
                  </div>
                </div>
              </template>
            </FullCalendar>
          </div>
        </div>
      </div>
    </div>
    <!-- <copy-day-modal @copy="onCopy" :ref="el => copyDayModal = el" />
    <copy-week-modal @copy="copyWeek" :ref="el => copyWeekModal = el" /> -->
    <plan-edit-modal v-if="showPlanEditModal"
      v-model="showPlanEditModal"
      :office-id="inputs.selectedOfficeId"
      :staffs="staffs || []"
      :users="users || []"
      :plan="plan"
      :editable="true"
      :params="params"
      :is-shift-mode="isShiftMode"
      @update:modelValue="val => showPlanEditModal = val"
      @close="onPlanEditModalClosed"
      @cancel="closeModal" />
    <CBModal :ref="el => dialog = el" />
  </div>
</template>

<script setup>
import SelectOffice from '@/components/SelectOffice'
import StaffSearch from '@/components/StaffSearch'
import StaffCheckBoxList from '@/components/StaffCheckBoxList'
import PlanEditModal from '@/components/PlanEditModal'
import CBModal from '@/components/CBModal'
import FullCalendar from '@fullcalendar/vue3'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import luxonPlugin from '@fullcalendar/luxon'
import locale from '@fullcalendar/core/locales/ja'
import _ from 'lodash'
import { useStore } from 'vuex'
import { ref, reactive, computed, watch, onMounted } from 'vue'
import dayjs from 'dayjs'
import constants from '@/constants'
import api from '@/libs/Api'
import utils from '@/libs/Utils'
import { useExecutor } from '@/libs/Executor'
import Constants from '@/constants'

const DATE_RANGE = 7

const { exec } = useExecutor()
const store = useStore()
const plan = ref(null)
const showPlanEditModal = ref(false)
const dialog = ref(null)
const copyDayModal = ref(null)
const copyWeekModal = ref(null)
const isProcessing = ref(false)
const isWeekMode = ref(true)
const isShiftMode = ref(false)
const params = ref({
  start_day: null,
  start_hhmm: null,
  end_day: null,
  end_hhmm: null,
  staff_id: null,
  isShiftMode: false
})
const inputs = reactive({
  selectedOfficeId: null,
  selectedStaffs: [],
  userViewMode: false
})
const dateRange = reactive({
  startDate: null,
  endDate: null
})
const tipPlace = ref({
  top: 0,
  left: 0
})
const tip = reactive({
  active: false,
  event: null,
  place: null
})
const calendarRef = ref(null)
const calendarEvents = ref([])
const calendarResources = ref([])

/**
 * Converts a plan object to calendar event format
 * @param {Object} plan - The plan data to convert
 * @returns {Array} Array of event objects for the calendar
 */
function convertPlan2Event (plan) {
  let allEvents = []
  _.forEach(plan.staff, staff => {
    const mark = plan.staff.length > 1 ? '*' : ''
    const staffId = staff.id.toString()

    // Check if this is an overnight event
    const startTimeValue = parseInt(plan.service.start_hhmm)
    const endTimeValue = parseInt(plan.service.end_hhmm)
    const isCrossDayEvent = startTimeValue > endTimeValue && plan.service.start_day === plan.service.end_day

    // Calculate the actual end day
    const actualEndDay = isCrossDayEvent
      ? dayjs(plan.service.end_day, 'YYYYMMDD').add(1, 'day').format('YYYYMMDD')
      : plan.service.end_day

    if (isCrossDayEvent) {
      // First day event: from start time to midnight (23:59)
      const firstDayEvent = {
        id: `${plan.id}_${staffId}_day1`,
        title: `${mark}${plan.user.display_name}`,
        groupId: plan.id,
        resourceId: staffId,
        start: `${utils.formatDay(plan.service.start_day)}T${utils.formatHHMM(plan.service.start_hhmm)}:00`,
        end: `${utils.formatDay(plan.service.start_day)}T24:00:00`,
        tag: plan,
        userDisplayName: plan.user.display_name,
        service: convertService(plan.service),
        caretime: `${utils.formatHHMM(plan.service.start_hhmm)} ～ 24:00`,
        color: utils.getEventColor(plan)
      }

      // Second day event: from midnight (00:00) to end time
      const secondDayEvent = {
        id: `${plan.id}_${staffId}_day2`,
        title: `${mark}${plan.user.display_name}`,
        groupId: plan.id,
        resourceId: staffId,
        start: `${utils.formatDay(actualEndDay)}T00:00:00`,
        end: `${utils.formatDay(actualEndDay)}T${utils.formatHHMM(plan.service.end_hhmm)}:00`,
        tag: plan,
        userDisplayName: plan.user.display_name,
        service: convertService(plan.service),
        caretime: `00:00 ～ ${utils.formatHHMM(plan.service.end_hhmm)}`,
        color: utils.getEventColor(plan)
      }

      allEvents.push(firstDayEvent)
      allEvents.push(secondDayEvent)
    } else {
      // Regular single-day event
      const event = {
        id: `${plan.id}_${staffId}`,
        title: `${mark}${plan.user.display_name}`,
        groupId: plan.id,
        resourceId: staffId,
        start: `${utils.formatDay(plan.service.start_day)}T${utils.formatHHMM(plan.service.start_hhmm)}:00`,
        end: `${utils.formatDay(actualEndDay)}T${utils.formatHHMM(plan.service.end_hhmm)}:00`,
        tag: plan,
        userDisplayName: plan.user.display_name,
        service: convertService(plan.service),
        caretime: `${utils.formatHHMM(plan.service.start_hhmm)} ～ ${utils.formatHHMM(plan.service.end_hhmm)}`,
        color: utils.getEventColor(plan)
      }

      allEvents.push(event)
    }
  })

  return allEvents
}

const convertService = (service) => {
  let isTeresa = (Constants.FunctionMask.TeresaStandard & profile.value.site.function_mask) === Constants.FunctionMask.TeresaStandard
  if (isTeresa) {
    if (service.service_teresa_unique_code) {
      return service.service_teresa_unique_code?.display_name
    }
  }
  if (service.service_code) {
    return service.service_code.display_name
  }

  return service.service_type.display_name
}

/**
 * Converts API result data to calendar events format
 * @param {Array} result - The API result containing plans
 * @returns {Array} Flattened array of calendar events
 */
function convertResultToEvents (result) {
  return _.chain(result)
    .map(plan => convertPlan2Event(plan))
    .flatten(true)
    .value()
}

// Computed properties
// eslint-disable-next-line no-unused-vars
const hasTarget = computed(() => {
  return inputs.selectedOfficeId && inputs.selectedStaffs.length > 0
})

const calendarApi = computed(() => {
  return calendarRef.value.getApi()
})

const queryParams = computed(() => {
  const staffIds = _.map(inputs.selectedStaffs, selectedStaff => selectedStaff.id)
  const params = {
    mode: constants.QUERY_MODE_SHIFT,
    start_day_from: dateRange.startDate ? dateRange.startDate.format('YYYYMMDD') : null,
    start_day_to: dateRange.endDate ? dateRange.endDate.format('YYYYMMDD') : null
  }
  if (inputs.selectedOfficeId) {
    params.office_id = inputs.selectedOfficeId
  }
  if (staffIds.length > 0) {
    params.staff_ids = staffIds
  }
  if (inputs.userViewMode && users.value.length > 0) {
    params.user_ids = _.map(users.value, user => user.id)
  }
  return params
})

// Vuex state/getters
const selectedOfficeId = computed(() => store.state.staffShift.selectedOfficeId)
const profile = computed(() => store.state.session.profile)
const users = computed(() => store.getters['staffShift/users'])
const staffs = computed(() => store.getters['staffShift/staffs'])
const selectedStaffs = computed(() => store.getters['staffShift/selectedStaffs'])

// Event handlers
const mousemove = (e) => {
  tipPlace.value.top = (e.clientY - e.offsetY) - 70 + 'px'
  tipPlace.value.left = (e.clientX - e.offsetX) - 450 + 'px'
}

// Mouseover event handler
const onMouseover = (e) => {
  tip.active = true
  tip.event = e.event
}

// Mouseout event handler
const onMouseout = () => {
  tip.active = false
}

// Date click event handler
const onDateClick = (info) => {
  if (isProcessing.value || inputs.selectedStaffs.length < 1) return

  // Get the staff ID from the resource if available
  let staffId = null
  if (info.resource && info.resource.id) {
    staffId = info.resource.id
  } else {
    // In case resource isn't available, try to determine which staff cell was clicked
    const resourceId = info.jsEvent?.target?.closest('.fc-timegrid-col')?.getAttribute('data-resource-id')
    if (resourceId) {
      staffId = resourceId
    } else if (inputs.selectedStaffs.length === 1) {
      // If only one staff is selected, default to that staff
      staffId = inputs.selectedStaffs[0].id.toString()
    } else {
      // If we can't determine the staff, show an alert
      dialog.value.show({
        title: '確認',
        text: 'スタッフが特定できません。1人のスタッフを選択してください。',
        clickToClose: true,
        buttons: [
          {
            title: '閉じる',
            handler: () => {
              dialog.value.hide()
            }
          }
        ]
      })
      return
    }
  }

  params.value = {
    start_day: dayjs(info.date).format('YYYY-MM-DD'),
    start_hhmm: dayjs(info.date).format('HHmm'),
    end_day: dayjs(info.date).format('YYYY-MM-DD'),
    end_hhmm: dayjs(info.date).add(30, 'minutes').format('HHmm'),
    staff_id: staffId
  }
  isShiftMode.value = true
  plan.value = null
  showPlanEditModal.value = true
}

// Event click outer handler (for status checks)
const onEventClickOuter = (info) => {
  if (isProcessing.value) return
  const prev = info.event.extendedProps.tag
  if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
    dialog.value.show({
      title: '確認',
      text: '承認が済んでいるプランです。',
      clickToClose: false,
      buttons: [
        {
          title: '閉じる',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        }
      ]
    })
  } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
    dialog.value.show({
      title: '確認',
      text: '報告が済んでいるプランです。編集しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            onEventClick(info)
          }
        }
      ]
    })
  } else {
    onEventClick(info)
  }
}

// Event click event handler
const onEventClick = (info) => {
  params.value = null
  plan.value = info.event.extendedProps.tag
  isShiftMode.value = true
  showPlanEditModal.value = true
}

// Event resize event handler
const onEventResize = (info) => {
  if (isProcessing.value) return
  const prev = info.event.extendedProps.tag
  const endHH = dayjs(info.event.end).format('HHmm')
  if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
    dialog.value.show({
      title: '確認',
      text: '承認が済んでいるプランです。',
      clickToClose: false,
      buttons: [
        {
          title: '閉じる',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        }
      ]
    })
  } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
    dialog.value.show({
      title: '確認',
      text: '報告が済んでいるプランです。時間を変更しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            const plan = convertEvent2Plan(info.event)
            registerPlan(plan)
          }
        }
      ]
    })
  } else if (endHH !== prev.service.end_hhmm) {
    dialog.value.show({
      title: '確認',
      text: '元の時間と異なりますが変更しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            const plan = convertEvent2Plan(info.event)
            registerPlan(plan)
          }
        }
      ]
    })
  }
}

// Event drop event handler
const onEventDropOuter = (info) => {
  if (isProcessing.value) return
  const prev = info.event.extendedProps.tag
  const startHH = dayjs(info.event.start).format('HHmm')
  const startDay = dayjs(info.event.start).format('YYYYMMDD')
  if (Number(prev.plan_status_cd) === Number(constants.Code.PlanStatus.APPROVED)) {
    dialog.value.show({
      title: '確認',
      text: '承認が済んでいるプランです。',
      clickToClose: false,
      buttons: [
        {
          title: '閉じる',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        }
      ]
    })
  } else if (Number(prev.plan_status_cd) >= Number(constants.Code.PlanStatus.REPORTED)) {
    dialog.value.show({
      title: '確認',
      text: '報告が済んでいるプランです。時間を移動してもよろしいですか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            onEventDrop(info)
          }
        }
      ]
    })
  } else if (startDay !== prev.service.start_day || startHH !== prev.service.start_hhmm) {
    dialog.value.show({
      title: '確認',
      text: '元の時間と異なりますが移動しますか？',
      clickToClose: false,
      buttons: [
        {
          title: 'キャンセル',
          handler: () => {
            dialog.value.hide()
            info.revert()
          }
        },
        {
          title: 'はい',
          handler: () => {
            dialog.value.hide()
            onEventDrop(info)
          }
        }
      ]
    })
  } else {
    onEventDrop(info)
  }
}

// Event drop event handler
const onEventDrop = (info) => {
  const plan = convertEvent2Plan(info.event)
  if (info.newResource) {
    const newStaff = _.find(staffs.value, staff => {
      return staff.id === Number(info.newResource.id)
    })
    if (!newStaff) return
    const planStaffList = _.filter(plan.staff, staff => {
      return staff.staff_id !== Number(info.oldResource.id)
    })
    planStaffList.push({ staff_id: newStaff.id })
    plan.staff = planStaffList
  }
  registerPlan(plan)
}

// Event dates render event handler
const onDatesRender = (info) => {
  const newStart = dayjs(info.view.activeStart)
  const newEnd = dayjs(info.view.activeEnd).add(-1, 'days')
  const unchanged = dateRange.startDate
    && dateRange.startDate.format('YYYYMMDD') === newStart.format('YYYYMMDD')
    && dateRange.endDate
    && dateRange.endDate.format('YYYYMMDD') === newEnd.format('YYYYMMDD')
  dateRange.startDate = newStart
  dateRange.endDate = newEnd
  if (!unchanged) loadPlans()
}

/**
     * Formats a date object to display time for events
     * @param {Date|string} dateObj - Date object or string to format
     * @returns {string} Formatted time string (HH:MM)
     */
const formatEventTime = (dateObj) => {
  if (!dateObj) return ''
  const date = new Date(dateObj)
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
     * Loads plans from API based on current parameters
     * @returns {Promise} Promise resolving to loaded plans
     */
const loadPlans = () => {
  return exec(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return

    if (!inputs.selectedOfficeId || inputs.selectedStaffs.length < 1) {
      calendarEvents.value = []
      return
    }

    if (inputs.userViewMode && users.value.length < 1) {
      calendarEvents.value = []
      return
    }

    isProcessing.value = true
    try {
      const result = await api.getPlans(queryParams.value)
      calendarEvents.value = result || []
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Handler for copy completion event
     * @param {*} dst - Destination data (unused)
     */
// eslint-disable-next-line no-unused-vars
const onCopy = (dst) => {
  loadPlans()
}

/**
     * Handler for plan edit modal close
     * @param {boolean} updated - Whether the plan was updated
     */
const onPlanEditModalClosed = (updated) => {
  if (!updated) return
  loadPlans()
}

/**
     * Adds a staff to the selected staff list
     * @param {Object} selectedStaff - Staff object to add
     */
const addSelectedStaff = (selectedStaff) => {
  if (!selectedStaff) return

  const index = _.findIndex(inputs.selectedStaffs, staff => staff.id === selectedStaff.id)
  if (index >= 0) return

  // Create a new array instead of directly pushing to maintain reactivity in Vue 3
  inputs.selectedStaffs = utils.sortStaffsByKana([...inputs.selectedStaffs, selectedStaff])

  // Load plans with the new staff selection
  loadPlans()
}

/**
     * Copies a week's shifts to another week
     * @param {Object} destWeek - Destination week
     */
// eslint-disable-next-line no-unused-vars
const copyWeek = (destWeek) => {
  exec(async () => {
    isProcessing.value = true
    try {
      const params = queryParams.value
      params.dest_start_day_from = destWeek.format('YYYYMMDD')
      await api.copyShiftWeek(params)
      await loadPlans()
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Shows the copy week modal
     */
// eslint-disable-next-line no-unused-vars
const showCopyWeekModal = () => {
  if (isProcessing.value) return
  copyWeekModal.value.show({
    weekStart: dateRange.startDate
  })
}

/**
     * Shows the copy day modal
     */
// eslint-disable-next-line no-unused-vars
const showCopyDayModal = () => {
  if (isProcessing.value) return
  copyDayModal.value.show({
    targetDay: dateRange.startDate
  })
}

/**
     * Copies a day's shifts to another day
     * @param {Object} destDay - Destination day
     */
// eslint-disable-next-line no-unused-vars
const copyDay = (destDay) => {
  exec(async () => {
    isProcessing.value = true
    try {
      const params = queryParams.value
      params.dest_start_day_from = destDay.format('YYYYMMDD')
      await api.copyShiftDay(params)
      await loadPlans()
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Registers/saves a plan
     * @param {Object} plan - Plan object to save
     */
const registerPlan = (plan) => {
  exec(async () => {
    isProcessing.value = true
    try {
      await store.dispatch('careplan/savePlan', plan)
      await loadPlans()
    } finally {
      isProcessing.value = false
    }
  })
}

/**
     * Handler for user view mode change
     * @returns {Promise} Promise resolving after mode change
     */
const onChangeUserViewMode = () => {
  return exec(async () => {
    await store.dispatch('staffShift/loadUsers', { officeId: inputs.selectedOfficeId, userViewMode: inputs.userViewMode })
    return loadPlans()
  })
}

/**
     * Handler for office selection
     * @param {string|number} officeId - ID of the selected office
     */
const onOfficeSelected = (officeId) => {
  if (officeId) {
    loadPlans()
  }
}

/**
     * Converts a calendar event to plan format
     * @param {Object} event - Calendar event to convert
     * @returns {Object} Plan object
     */
const convertEvent2Plan = (event) => {
  const plan = event.extendedProps.tag
  return {
    id: plan.id,
    user_id: plan.user_id,
    office_id: plan.office_id,
    staff: _.map(plan.staff, staff => {
      return { staff_id: staff.id }
    }),
    service: {
      service_type_id: plan.service.service_type_id,
      start_day: dayjs(event.start).format('YYYYMMDD'),
      start_hhmm: dayjs(event.start).format('HHmm'),
      end_day: dayjs(event.end).format('YYYYMMDD'),
      end_hhmm: dayjs(event.end).format('HHmm')
    }
  }
}

// Watchers
watch(() => inputs.selectedOfficeId, (selectedOfficeId) => {
  exec(() => {
    isProcessing.value = true
    return store.dispatch('staffShift/selectOffice', {
      officeId: selectedOfficeId,
      userViewMode: inputs.userViewMode
    })
      .then(() => {
        inputs.selectedOfficeId = selectedOfficeId
        inputs.selectedStaffs = selectedStaffs.value
      })
      .finally(() => {
        isProcessing.value = false
      })
  })
})

watch(() => inputs.selectedStaffs, (newSelectedStaffs) => {
  // Update resources for calendar（resourceOrder: 'sort' 用に sort を付与）
  calendarResources.value = utils.sortStaffsByKana(newSelectedStaffs).map((staff, index) => {
    return {
      id: staff.id.toString(), // Ensure ID is a string
      title: staff.display_name,
      sort: index
    }
  })

  // Update store state
  store.dispatch('staffShift/setSelectedStaffs', {
    officeId: inputs.selectedOfficeId,
    staffs: newSelectedStaffs
  })

  if (newSelectedStaffs.length > 0) {
    loadPlans()
  } else {
    calendarEvents.value = []
    calendarRef.value?.getApi()?.refetchEvents()
  }
}, { deep: true })

watch(() => users.value, (newUsers) => {
  if (inputs.userViewMode && newUsers && newUsers.length > 0) {
    loadPlans()
  }
})

watch(calendarEvents, () => {
  calendarRef.value?.getApi()?.refetchEvents()
})

watch(isProcessing, (val) => {
  const api = calendarRef.value?.getApi()
  if (api) {
    api.setOption('editable', !val)
    api.setOption('droppable', !val)
  }
})

const fetchCalendarEvents = (_fetchInfo, successCallback) => {
  successCallback(convertResultToEvents(calendarEvents.value))
}

// Calendar options as a computed property
const calendarOptions = computed(() => {
  return {
    schedulerLicenseKey: process.env.VUE_APP_SCHEDULER_LICENSE_KEY,
    datesAboveResources: true,
    allDaySlot: false,
    editable: true,
    droppable: true,
    dayMaxEvents: true,
    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    scrollTime: '08:00:00',
    initialView: 'resourceTimeGridWeek',
    timeZone: 'Asia/Tokyo',
    plugins: [
      luxonPlugin,
      interactionPlugin,
      resourceTimeGridPlugin
    ],
    resources: calendarResources.value,
    resourceOrder: 'sort',
    locale: locale,
    views: {
      resourceTimeGridWeek: {
        type: 'resourceTimeGrid',
        duration: { days: DATE_RANGE },
        buttonText: '週',
        titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }
      },
      resourceTimeGridDay: {
        type: 'resourceTimeGrid',
        duration: { days: 1 },
        buttonText: '日',
        titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }
      }
    },
    headerToolbar: {
      left: 'prev,today,next',
      center: 'title',
      right: 'resourceTimeGridWeek,resourceTimeGridDay'
    },
    events: fetchCalendarEvents,
    customButtons: {
      prev: {
        text: '<',
        click: function () {
          calendarApi.value.prev()
        }
      },
      today: {
        text: '今週',
        click: function () {
          calendarApi.value.today()
        }
      },
      next: {
        text: '>',
        click: function () {
          calendarApi.value.next()
        }
      },
      copy: {
        text: 'コピー',
        click: function () {
          if (isWeekMode.value) {
            showCopyWeekModal()
          } else {
            showCopyDayModal()
          }
        }
      },
      resourceTimeGridWeek: {
        text: '週',
        click: function () {
          if (isWeekMode.value) return
          calendarApi.value.changeView('resourceTimeGridWeek')
          calendarApi.value.gotoDate(dayjs().day(1).toDate())
          isWeekMode.value = true
          calendarApi.value.setOption('customButtons', {
            ...calendarApi.value.getOption('customButtons'),
            today: {
              text: '今週',
              click: function () {
                calendarApi.value.today()
              }
            }
          })
        }
      },
      resourceTimeGridDay: {
        text: '日',
        click: function () {
          if (!isWeekMode.value) return
          calendarApi.value.changeView('resourceTimeGridDay')
          calendarApi.value.gotoDate(dayjs().toDate())
          isWeekMode.value = false
          calendarApi.value.setOption('customButtons', {
            ...calendarApi.value.getOption('customButtons'),
            today: {
              text: '今日',
              click: function () {
                calendarApi.value.today()
              }
            }
          })
        }
      }
    },
    firstDay: 1,
    initialDate: dayjs().startOf('week').add(1, 'day').toDate(),
    weekends: true,
    dateClick: onDateClick,
    eventClick: onEventClickOuter,
    eventMouseEnter: onMouseover,
    eventMouseLeave: onMouseout,
    eventResize: onEventResize,
    eventDrop: onEventDropOuter,
    datesSet: onDatesRender // version 6.10.0 changed from datesRender to datesSet
  }
})
// Lifecycle hooks
onMounted(() => {
  exec(async () => {
    inputs.selectedOfficeId = selectedOfficeId.value

    // Initialize dateRange with default values (current week)
    if (!dateRange.startDate || !dateRange.endDate) {
      const today = dayjs()
      // We want to make sure today is visible in the view
      const currentDay = today.day()
      // Calculate to get the nearest Monday
      const diff = currentDay === 0 ? 6 : currentDay - 1
      const weekStart = today.subtract(diff, 'day')
      const weekEnd = weekStart.add(6, 'days')

      dateRange.startDate = weekStart
      dateRange.endDate = weekEnd
    }

    await store.dispatch('staffShift/initStaffShift')

    // Ensure initial office selection is set from store
    if (selectedOfficeId.value && !inputs.selectedOfficeId) {
      inputs.selectedOfficeId = selectedOfficeId.value

      // If office is selected but no staffs, try to get staffs from store
      if (selectedStaffs.value && selectedStaffs.value.length > 0) {
        inputs.selectedStaffs = selectedStaffs.value
      }
    }
  })
})

</script>
<style lang="scss">
// Calendar layout and sizing
.card-body.staff-shift {
  height: 650px;
  position: relative;
  overflow: hidden;
  .fc {
    height: 100%;
    position: relative;
    colgroup col {
      width: 100px;
    }
    .fc-scroller-harness .fc-scroller table {
      colgroup col {
        width: 80px !important;
      }
      thead tr {
        .fc-timegrid-axis {
          border-right: 1px solid black !important;
          border-left: none !important;
          border-top: none !important;
          border-bottom: none !important;
        }
        .fc-col-header-cell.fc-resource {
          border-bottom: none !important;
          border-top: 1px solid black !important;
        }
      }
    }
  }
  // FullCalendar の本文ラッパー: 帯内にスクロールバーを出さない
  .fc-event-main {
    overflow: hidden !important;
    max-width: 100%;
  }
}

// View-specific layouts
.fc {
  // View container
  &-view-harness {
    height: 600px !important;
    overflow-y: auto !important;
  }

  // Week view
  &-resourceTimeGridWeek-view {
    overflow-x: auto;

    table {
      min-width: 100%;
      border: none !important;
      width: 6300px !important;
    }
  }

  // Day view
  &-resourceTimeGridDay-view {
    overflow-x: auto;

    table {
      min-width: 100%;
      border: none !important;
      width: 900px !important;
    }
  }
  // Scrolling behavior
  &-timegrid-body {
    overflow: hidden !important;
  }
  &-direction-ltr .fc-timegrid-col-events {
    margin: 0 !important;
  }
  // Header styles
  &-col-header-cell.fc-day {
    background-color: #f2f7f8 !important;
    border: {
      left: 1px solid black !important;
      right: 1px solid black !important;
      bottom: 1px solid black !important;
    }
    box-sizing: border-box !important;
    &.fc-day-today {
      background-color: #f2f4f8 !important;
    }
  }
  // Today highlighting
  &-timegrid-col.fc-day-today,
  &-timegrid-col.fc-day-today .fc-timegrid-col-frame,
  &-timegrid-slots tr .fc-timegrid-slot.fc-day-today {
    background-color: #f2f4f8 !important;
  }
  // Border styles
  &-timegrid-col,
  &-resource,
  &-timegrid-axis:first-child,
  &-timegrid-slot {
    border-right: 1px solid black !important;
  }
  // Staff resource header
  .staff-resource {
    border-bottom: none;
    border-right: 1px solid black !important;
  }
  // Event styles
  &-event {
    border-radius: 0px;
    border: none;
    cursor: move;
    color: #ffffff !important;
    font-size: 12px !important;
    margin: 0 !important;
    padding: 3px 4px !important;
    text-align: center;
    overflow: hidden;
    white-space: normal;
    line-height: 1.25;
  }
  &-timegrid-event-harness {
    margin: 0 1px !important;
  }
  // Toolbar buttons
  &-prev-button,
  &-today-button,
  &-next-button,
  &-resourceTimeGridWeek-button,
  &-resourceTimeGridDay-button {
    background: #ffffff !important;
    border: 1px solid rgba(120, 130, 140, 0.13) !important;
    color: #99abb4 !important;
    text-transform: capitalize !important;
    height: 2.1em !important;
    &:hover {
      background: #f2f4f8 !important;
      opacity: 0.8 !important;
    }
  }
  // Title and day text styles
  &-toolbar-chunk .fc-toolbar-title {
    font-size: 18px;
    font-weight: 500;
    line-height: 30px;
    text-transform: uppercase;
  }
  &-day div a {
    font-size: 13px;
    font-weight: 300;
    line-height: 20px;
    color: black;
    text-decoration: none;
  }
}

// Event content formatting（時間は1行・省略。利用者名のみ帯幅で折り返し）
.fc-event-main-content {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  .fc-event-time-title {
    display: block;
    font-size: 11px;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fc-event-time-title.fc-event-user-name {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: normal;
    text-overflow: clip;
  }
  // Tooltip behavior
  &:hover {
    &::after {
      content: attr(title);
      position: absolute;
      bottom: 120%;
      left: 50%;
      transform: translateX(-25%);
      width: 200px;
      padding: 5px 10px;
      background-color: rgba(133, 51, 51, 0.8) !important;
      color: #fff;
      border-radius: 4px;
      font-size: 12px;
      z-index: 1000;
      text-align: left;
      white-space: pre-line;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    }
    &::before {
      content: '';
      position: absolute;
      bottom: 120%;
      left: 50%;
      transform: translateX(-50%);
      margin-bottom: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
      z-index: 1000;
    }
  }
}

.staff-shift .fc-view-harness .fc-resourceTimeGridWeek-view table tbody tr td .fc-scroller-harness .fc-scroller-liquid-absolute {
  position: static !important;
  overflow: visible !important;
}

.event-type small {
  font-size: 10px;
  opacity: 0.9;
  display: block;
  line-height: 1.1;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: normal;
}

.tooltipevent {
  width: 250px;
  height: 100px;
  padding: 12px;
  background: #aed0ea;
  position: absolute;
  z-index: 99999 !important;
  top: 0;
  left: 0;
  font-size: 0.8rem;
  box-shadow: 1px 1px 3px 0px #888888;
  line-height: 1rem;
  border-radius: 4px;
  pointer-events: none;
}

.h-83vh {
  height: 83vh !important;
}

.h-100 {
  height: 100% !important;
}

</style>
